/**
 * AUTH-002 — the fetch the browser Supabase client uses for every request.
 *
 * supabase-js sends PostgREST, RPC, Storage, Functions and Auth requests through
 * `global.fetch`; this wrapper is that fetch. It adds three things:
 *
 *   1. Token refresh (`/auth/v1/token?grant_type=refresh_token`): a 429 opens the
 *      back-off gate and is reported to auth-js as a network error, so the session is
 *      kept and nothing retries on the wire until the gate closes (§6.1 item 6).
 *   2. Data requests (everything outside `/auth/v1/`): if no tab has seen input for 10
 *      minutes the request is not sent; the session ends instead (§5 "wake").
 *   3. Data requests that come back 401: one refresh, then the same request is resent
 *      with the new token (§6.1 item 4). A 401 means PostgREST/Storage refused the JWT
 *      before running anything, so resending a write is safe. If the refresh token is
 *      dead the controller signs out.
 *
 * Token freshness before a data request needs no code here: supabase-js computes the
 * Authorization header from `auth.getSession()`, which refreshes inside the library's
 * 90 s margin — more than the 60 s the policy asks for.
 *
 * Pure: the base fetch, the gate and the controller are injected. The controller is read
 * lazily (it is created after the client) and may be absent: then requests pass through.
 */
import type { RefreshGate } from './refresh-gate';
import type { SessionController } from './session-controller';
import {
  REFRESH_RATE_LIMITED_MESSAGE,
  SESSION_ENDED_MESSAGE,
  SESSION_EXPIRED_CODE,
} from './session-policy';

type FetchFn = typeof fetch;

function urlOf(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  return input.url;
}

function bearerOf(headers: Headers): string | null {
  const v = headers.get('Authorization');
  return v && v.toLowerCase().startsWith('bearer ') ? v.slice(7).trim() : null;
}

/** A local 401 shaped like a route's SESSION_EXPIRED answer; used when a request is not sent. */
export function sessionEndedResponse(): Response {
  return new Response(
    JSON.stringify({ error: SESSION_ENDED_MESSAGE, code: SESSION_EXPIRED_CODE }),
    {
      status: 401,
      headers: { 'Content-Type': 'application/json' },
    }
  );
}

export function createSupabaseFetch(deps: {
  baseFetch: FetchFn;
  gate: RefreshGate;
  controller: () => SessionController | null;
  /** The anon key: a 401 on a request that carried it is not a session problem. */
  anonKey?: string;
}): FetchFn {
  return async function supabaseFetch(
    input: RequestInfo | URL,
    init?: RequestInit
  ): Promise<Response> {
    const url = urlOf(input);

    if (url.includes('/auth/v1/')) {
      if (url.includes('/auth/v1/token') && url.includes('grant_type=refresh_token')) {
        // Thrown TypeErrors become AuthRetryableFetchError in auth-js: session kept, no SIGNED_OUT.
        if (deps.gate.isOpen()) throw new TypeError(REFRESH_RATE_LIMITED_MESSAGE);
        const res = await deps.baseFetch(input, init);
        if (res.status === 429) {
          deps.gate.trip();
          throw new TypeError(REFRESH_RATE_LIMITED_MESSAGE);
        }
        if (res.ok) deps.gate.reset();
        return res;
      }
      return deps.baseFetch(input, init);
    }

    const controller = deps.controller();
    // The session ended in this tab and the page is on its way to /login: send nothing.
    if (controller?.isEnding()) return sessionEndedResponse();
    if (!controller || !controller.isActive()) return deps.baseFetch(input, init);

    if (controller.isIdleExpired()) {
      await controller.endSession('idle');
      return sessionEndedResponse();
    }

    const res = await deps.baseFetch(input, init);
    if (res.status !== 401) return res;

    const headers = new Headers(
      init?.headers ?? (input instanceof Request ? input.headers : undefined)
    );
    const sent = bearerOf(headers);
    // Only a request that carried a user token can be fixed by refreshing it.
    if (!sent || (deps.anonKey && sent === deps.anonKey)) return res;
    // A Request object's body may already be consumed; supabase-js always passes (url, init).
    if (input instanceof Request) return res;

    const fresh = await controller.refreshAfter401(sent);
    if (fresh.status === 'ended') return sessionEndedResponse();
    if (fresh.status !== 'ok') return res;

    headers.set('Authorization', `Bearer ${fresh.token}`);
    return deps.baseFetch(input, { ...init, headers });
  };
}
